/**
 * lifecycle-bridge.ts — `ss upgrade` / `ss doctor` ⇄ the lifecycle audit.
 *
 * The lifecycle invariants (module out of sync with its BA, generator epoch
 * drift, code written outside the protected regions, stale BA verdicts…) live
 * in ONE engine: the `audit-lifecycle` skill CLI. The CLI never re-implements
 * them: it runs that CLI as a CHILD process — never imported, the skills run
 * in the Zod v4 world and `src/` in v3 — dry-run (no verdict file written) and
 * read-only, then reads its envelope.
 *
 * - `ss upgrade` asks it, after the regeneration, WHICH developed modules must
 *   be resumed with the new generators (LIFE-001 / LIFE-002) and records them
 *   in `.smartstack/upgrade-report.json` (`modulesToResume`), plus the code at
 *   risk before that resume (LIFE-007).
 * - `ss doctor` shows the non-ok findings as « Cycle de vie » warnings.
 *
 * Fail-soft: a missing skill, a crash or an unreadable envelope is a
 * `ran: false` with its reason — never a thrown error, never a fake green.
 */
import fs from 'fs-extra';
import { tmpdir } from 'os';
import { join } from 'path';
import { defaultRunner, parseEnvelope, type CommandRunner } from './upgrade-verify.js';

export type LifecycleSeverity = 'ok' | 'warn' | 'err';

export interface LifecycleBridgeFinding {
  ruleId: string;
  severity: LifecycleSeverity;
  message: string;
  /** `project` or `APP/MODULE`. */
  scope: string;
  evidence: string[];
  remedy?: { lane: string; action: string };
}

export interface LifecycleBridgeResult {
  ran: boolean;
  /** Why it did not run (ran=false). */
  reason?: string;
  exitClass?: string;
  findings: LifecycleBridgeFinding[];
}

export interface ModuleToResume {
  app: string;
  module: string;
  /** One line per finding that schedules the resume. */
  reasons: string[];
}

/** The rules whose findings mean « re-run /ba-develop on this module ». */
export const RESUME_RULES: readonly string[] = ['LIFE-001', 'LIFE-002'];

/** The rules `ss upgrade` asks: what to resume, and the code a resume could erase. */
export const UPGRADE_RULES: readonly string[] = ['LIFE-001', 'LIFE-002', 'LIFE-007', 'LIFE-014'];

/** The rules `ss doctor` shows (cheap, read-only — LIFE-005 reads main's seed state with git show, never a diff). */
export const DOCTOR_RULES: readonly string[] = ['LIFE-001', 'LIFE-002', 'LIFE-005', 'LIFE-007', 'LIFE-009', 'LIFE-013', 'LIFE-015', 'LIFE-019', 'LIFE-020'];

export interface LifecycleBridgeOptions {
  projectDir: string;
  /** `templates/skills` of the CLI package. */
  skillsDir: string;
  /** Where `npx tsx` resolves from (the CLI package root). */
  toolCwd: string;
  rules?: readonly string[];
  run?: CommandRunner;
}

const SEVERITIES = new Set<LifecycleSeverity>(['ok', 'warn', 'err']);

function asFindings(raw: unknown): LifecycleBridgeFinding[] {
  if (!Array.isArray(raw)) return [];
  const out: LifecycleBridgeFinding[] = [];
  for (const f of raw as Array<Record<string, unknown>>) {
    if (typeof f?.ruleId !== 'string' || !SEVERITIES.has(f.severity as LifecycleSeverity)) continue;
    const remedy = f.remedy as { lane?: unknown; action?: unknown } | undefined;
    out.push({
      ruleId: f.ruleId,
      severity: f.severity as LifecycleSeverity,
      message: typeof f.message === 'string' ? f.message : '',
      scope: typeof f.scope === 'string' ? f.scope : 'project',
      evidence: Array.isArray(f.evidence) ? f.evidence.filter((e): e is string => typeof e === 'string') : [],
      ...(remedy && typeof remedy.lane === 'string' && typeof remedy.action === 'string' ? { remedy: { lane: remedy.lane, action: remedy.action } } : {}),
    });
  }
  return out;
}

/** Run the lifecycle audit of a project (dry-run, read-only). */
export async function runLifecycleAudit(opts: LifecycleBridgeOptions): Promise<LifecycleBridgeResult> {
  const cli = join(opts.skillsDir, 'audit-lifecycle', 'cli', 'audit-lifecycle', 'index.ts');
  if (!(await fs.pathExists(cli))) return { ran: false, reason: 'audit-lifecycle not found in the CLI package', findings: [] };
  if (!(await fs.pathExists(join(opts.projectDir, '.smartstack')))) return { ran: false, reason: 'not a SmartStack project (no .smartstack/)', findings: [] };
  const specFile = join(tmpdir(), `ss-lifecycle-${process.pid}-${Date.now()}.json`);
  try {
    await fs.writeFile(
      specFile,
      JSON.stringify({ projectRoot: opts.projectDir, dryRun: true, ...(opts.rules && opts.rules.length > 0 ? { rules: [...opts.rules] } : {}) }),
      'utf-8',
    );
    // Exit 1 (warn) / 2 (err) are verdicts, not failures: the envelope is read either way.
    const outcome = (opts.run ?? defaultRunner)(`npx --prefer-offline tsx "${cli}" --spec-file "${specFile}"`, opts.toolCwd);
    const env = parseEnvelope(outcome.output) as (Record<string, unknown> & { success?: boolean; errors?: string[] }) | null;
    if (!env) return { ran: false, reason: `unreadable output: ${outcome.output.trim().split('\n').slice(-3).join(' | ') || 'empty'}`, findings: [] };
    if (env.success === false) return { ran: false, reason: (env.errors ?? []).join(' | ') || 'audit-lifecycle refused the run', findings: [] };
    const report = (env.report ?? {}) as { findings?: unknown; exitClass?: unknown };
    return { ran: true, ...(typeof report.exitClass === 'string' ? { exitClass: report.exitClass } : {}), findings: asFindings(report.findings) };
  } finally {
    await fs.remove(specFile);
  }
}

/** The developed modules a non-ok LIFE-001 / LIFE-002 finding schedules for a resume, sorted. */
export function modulesToResume(findings: readonly LifecycleBridgeFinding[]): ModuleToResume[] {
  const byKey = new Map<string, ModuleToResume>();
  for (const f of findings) {
    if (!RESUME_RULES.includes(f.ruleId) || f.severity === 'ok') continue;
    const m = /^([^/\s]+)\/([^/\s]+)$/.exec(f.scope);
    if (!m) continue;
    const key = `${m[1]}/${m[2]}`;
    const entry = byKey.get(key) ?? { app: m[1], module: m[2], reasons: [] };
    entry.reasons.push(`${f.ruleId}: ${f.message}`);
    byKey.set(key, entry);
  }
  return [...byKey.values()].sort((a, b) => `${a.app}/${a.module}`.localeCompare(`${b.app}/${b.module}`));
}

/** Non-ok findings, one line each — what the report and the doctor show. */
export function findingLines(findings: readonly LifecycleBridgeFinding[]): string[] {
  return findings
    .filter((f) => f.severity !== 'ok')
    .map((f) => `${f.ruleId} [${f.severity}] ${f.scope === 'project' ? '' : `${f.scope} — `}${f.message}${f.remedy ? ` → ${f.remedy.action}` : ''}`);
}
